import { ReactElement, useMemo, useState } from 'react'
import { CardFlipProps } from './props'
import { StyledCardFlip, StyledFront, StyledBack } from './styles'
import { Text, Heading, Link, Image } from '../../Atoms'

const CardFlip = ({
    Icon,
    flipOnClick = true,
    flipOnHover,
    height = 200,
    image,
    link,
    onClick = () => undefined,
    textBack,
    textFront,
    linkText,
    variant,
}: CardFlipProps): ReactElement => {
    const [flipped, setFlipped] = useState(false)

    const frontHeading = useMemo(
        () => (
            <Heading
                size="6"
                css={{
                    alignSelf: 'start',
                }}
            >
                {textFront}
            </Heading>
        ),
        [textFront]
    )

    const frontImage = useMemo(
        () => (
            <Image
                src={image}
                css={{ maxSize: height - 100, placeSelf: 'center' }}
            />
        ),
        [height, image]
    )

    const frontContent = useMemo(() => {
        switch (variant) {
            case 'ImageHeader':
                return (
                    <>
                        {frontImage}
                        {frontHeading}
                    </>
                )
            default:
                return (
                    <>
                        {frontHeading}
                        {frontImage}
                    </>
                )
        }
    }, [frontHeading, frontImage, variant])

    const backText = useMemo(
        () => (
            <Text css={{ placeSelf: 'center' }} size="s">
                {textBack}
            </Text>
        ),
        [textBack]
    )

    const backContent = useMemo(() => {
        switch (variant) {
            case 'ImageHeader':
                return backText
            default:
                return (
                    <>
                        {backText}
                        <Link underline="none" href={link}>
                            <Text
                                css={{
                                    '&:hover': { color: '$primary500' },
                                }}
                                size="s"
                            >
                                {linkText} {Icon}
                            </Text>
                        </Link>
                    </>
                )
        }
    }, [Icon, backText, link, linkText, variant])

    return (
        <StyledCardFlip
            css={{ height: height }}
            data-testid="cardfliptest"
            flipped={flipped}
            onClick={(e) => {
                if (flipOnClick) setFlipped(!flipped)
                onClick(e)
            }}
            onMouseEnter={() => {
                if (flipOnHover) setFlipped(true)
            }}
            onMouseLeave={() => {
                if (flipOnHover) setFlipped(false)
            }}
        >
            <StyledFront variant={variant}>{frontContent}</StyledFront>
            <StyledBack variant={variant}>{backContent}</StyledBack>
        </StyledCardFlip>
    )
}

export default CardFlip
export { StyledCardFlip }
